/**
 * ContactList Component
 * Displays a list of conversations with search, filters and SLA indicators.
 * All data comes from Supabase contacts and messages via useConversations.
 */

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Search, User, Bot, UserCheck, Users, PanelLeftClose,
  FlaskConical, Clock, AlertTriangle, Brain, HelpCircle, Filter, MessageCircle,
  ChevronDown, ChevronRight, Loader2,
} from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Conversation, ConversationFilter, ConversationSourceFilter } from "../types"
import { formatTimestamp, truncateMessage, badgeStyles } from "../utils"
import { debounce } from "../utils"
import { categorizeConversation, parsePendingFlag, type ConversationCategory } from "../utils/conversationUtils"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { cn } from "@/lib/utils"

interface ContactListProps {
  conversations: Conversation[]
  loading: boolean
  selectedContactId: string | null
  onSelectContact: (contactId: string) => void
  filter: ConversationFilter
  onFilterChange: (filter: ConversationFilter) => void
  sourceFilter: ConversationSourceFilter
  onSourceFilterChange: (filter: ConversationSourceFilter) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  onCollapse?: () => void
  /** More message history exists beyond the loaded preview window. */
  hasMoreMessages?: boolean
  loadingMoreMessages?: boolean
  onLoadMoreMessages?: () => void
  /** A server-side message search is in flight. */
  searching?: boolean
}

export function ContactList({
  conversations,
  loading,
  selectedContactId,
  onSelectContact,
  filter,
  onFilterChange,
  sourceFilter,
  onSourceFilterChange,
  searchQuery,
  onSearchChange,
  onCollapse,
  hasMoreMessages = false,
  loadingMoreMessages = false,
  onLoadMoreMessages,
  searching = false,
}: ContactListProps) {
  const getHandledByFromModerationData = useCallback((raw: string | null | undefined): "bot" | "human" | null => {
    if (typeof raw !== "string" || !raw.trim()) return null
    try {
      const parsed = JSON.parse(raw) as { handled_by?: unknown }
      if (parsed.handled_by === "human" || parsed.handled_by === "bot") return parsed.handled_by
      return null
    } catch {
      return null
    }
  }, [])

  const OTHER_PAGE_SIZE = 25
  const [sortMode, setSortMode] = useState<"recent" | "sla">("sla")
  const [riskFilters, setRiskFilters] = useState<Set<string>>(new Set())
  const [channelFilters, setChannelFilters] = useState<Set<string>>(new Set())
  const [timeRange, setTimeRange] = useState<"all" | "24h" | "week" | "2weeks" | "month">("all")
  const [categorySearch, setCategorySearch] = useState("")
  const [expandedBadges, setExpandedBadges] = useState<Set<string>>(new Set())
  const [visibleOtherCount, setVisibleOtherCount] = useState(OTHER_PAGE_SIZE)
  // Pending and Resolved accumulate old entries nobody needs to re-review, so
  // they page the same way "Other"/New always has.
  const [visiblePendingCount, setVisiblePendingCount] = useState(OTHER_PAGE_SIZE)
  const [visibleResolvedCount, setVisibleResolvedCount] = useState(OTHER_PAGE_SIZE)
  // Collapsed state per section, keyed by category. Open starts expanded since
  // it's the moderator's to-do list; the others default open too but can be
  // tucked away once a moderator has triaged them.
  const [collapsedSections, setCollapsedSections] = useState<Set<ConversationCategory>>(new Set())
  const activeFilterCount = riskFilters.size + channelFilters.size + (sourceFilter === "real_only" ? 0 : 1)

  const toggleBadgeExpand = (contactId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedBadges(prev => {
      const next = new Set(prev)
      if (next.has(contactId)) { next.delete(contactId) } else { next.add(contactId) }
      return next
    })
  }

  const toggleRiskFilter = (value: string) => {
    setRiskFilters(prev => {
      const next = new Set(prev)
      if (next.has(value)) { next.delete(value) } else { next.add(value) }
      return next
    })
  }

  const toggleChannelFilter = (value: string) => {
    setChannelFilters(prev => {
      const next = new Set(prev)
      if (next.has(value)) { next.delete(value) } else { next.add(value) }
      return next
    })
  }

  const debouncedSearch = useCallback(debounce((value: string) => onSearchChange(value), 300), [onSearchChange])

  const getSlaInfo = (deadline?: number) => {
    if (!deadline) return null
    const now = Date.now() / 1000
    const remaining = deadline - now
    const isBreached = remaining <= 0
    if (isBreached) {
      const hoursAgo = Math.abs(Math.floor(remaining / 3600))
      return { text: hoursAgo > 0 ? `${hoursAgo}h overdue` : "Overdue", status: "breached" as const }
    }
    const hours = Math.floor(remaining / 3600)
    const mins  = Math.floor((remaining % 3600) / 60)
    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return { text: `${days}d left`, status: hours < 48 ? "warning" as const : "ok" as const }
    }
    if (hours > 0) return { text: `${hours}h ${mins}m`, status: hours < 2 ? "warning" as const : "ok" as const }
    return { text: `${mins}m`, status: "warning" as const }
  }

  const getConversationChannelLabel = useCallback((conversation: Conversation): string => {
    const channel = conversation.lastMessage?.channel
    if (!channel) return "Unknown"

    if (channel === "telerivet") {
      const integration = (conversation.lastMessage?.integration || {}) as Record<string, unknown>
      const routeNameRaw = typeof integration.route_name === "string" ? integration.route_name : undefined
      if (routeNameRaw) {
        const cleaned = routeNameRaw.replace(/\s*<[^>]+>\s*$/, "").trim()
        if (cleaned) return cleaned
      }
      return "Telerivet"
    }

    if (channel === "app") return "App"
    return channel.charAt(0).toUpperCase() + channel.slice(1)
  }, [])

  const timeCutoff = useMemo(() => {
    const now = Date.now() / 1000
    switch (timeRange) {
      case "24h":    return now - 24 * 3600
      case "week":   return now - 7 * 24 * 3600
      case "2weeks": return now - 14 * 24 * 3600
      case "month":  return now - 30 * 24 * 3600
      default:       return 0
    }
  }, [timeRange])

  const timeFilteredConversations = useMemo(() => {
    if (timeRange === "all" || timeCutoff === 0) return conversations
    return conversations.filter(c => {
      const ts = c.lastMessage ? new Date(c.lastMessage.created_at).getTime() / 1000 : 0
      return ts >= timeCutoff
    })
  }, [conversations, timeRange, timeCutoff])

  // All unique eval item names across all visible conversations, with counts
  const riskCategories = useMemo(() => {
    const counts = new Map<string, number>()
    timeFilteredConversations.forEach(c => {
      const items = c.flags?.evalItems || []
      items.forEach(item => counts.set(item.name, (counts.get(item.name) || 0) + 1))
    })
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }))
  }, [timeFilteredConversations])

  // All unique message channels across visible conversations, with counts
  const messageChannels = useMemo(() => {
    const counts = new Map<string, number>()
    timeFilteredConversations.forEach(c => {
      const label = getConversationChannelLabel(c)
      if (!label) return
      counts.set(label, (counts.get(label) || 0) + 1)
    })
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }))
  }, [timeFilteredConversations, getConversationChannelLabel])

  const filteredByChannel = useMemo(() => {
    if (channelFilters.size === 0) return timeFilteredConversations
    return timeFilteredConversations.filter(c => {
      const label = getConversationChannelLabel(c)
      return channelFilters.has(label)
    })
  }, [timeFilteredConversations, channelFilters, getConversationChannelLabel])

  const filteredByRisk = useMemo(() => {
    if (riskFilters.size === 0) return filteredByChannel
    return filteredByChannel.filter(c => {
      const now = Date.now() / 1000
      if (riskFilters.has("unread")      && (c.unreadCount || 0) > 0) return true
      if (riskFilters.has("has-sla")     && c.flags?.slaDeadline) return true
      if (riskFilters.has("breached")    && c.flags?.slaDeadline && c.flags.slaDeadline < now) return true
      if (riskFilters.has("needs-reply") && c.flags?.needsReply) return true

      // Risk level filter: match against contact-level severity
      const sev = c.flags?.contactSeverity ?? null
      if (sev !== null) {
        if (riskFilters.has("5") && sev >= 5) return true
        if (riskFilters.has("4") && sev >= 4 && sev < 5) return true
        if (riskFilters.has("3") && sev >= 3 && sev < 4) return true
        if (riskFilters.has("2") && sev >= 2 && sev < 3) return true
        if (riskFilters.has("1") && sev < 2) return true
      }

      // Category filter: match against any eval item on this contact
      const evalItems = c.flags?.evalItems || []
      if (evalItems.some(item => riskFilters.has(item.name))) return true

      return false
    })
  }, [filteredByChannel, riskFilters])

  // Compute a new sorted order, but freeze the contact ID order once established
  // so that polling updates (flag changes, last-message changes) don't shuffle
  // contacts around while the user is actively reading a conversation.
  // The order is only rebuilt when the *set* of contact IDs changes (new contact
  // arrives / contact disappears) or when the user explicitly changes sortMode.
  const sortedConversations = useMemo(() => {
    return [...filteredByRisk].sort((a, b) => {
      if (sortMode === "sla") {
        const aSla = a.flags?.slaDeadline || Infinity
        const bSla = b.flags?.slaDeadline || Infinity
        if (aSla !== bSla) return aSla - bSla
      }
      const aHasHighRisk = a.flags?.hasHighRisk ?? false
      const bHasHighRisk = b.flags?.hasHighRisk ?? false
      if (aHasHighRisk && !bHasHighRisk) return -1
      if (!aHasHighRisk && bHasHighRisk) return 1
      const aHasFlag = a.flags?.hasHighRisk || a.flags?.hasLowConfidence || a.flags?.hasAskedHuman
      const bHasFlag = b.flags?.hasHighRisk || b.flags?.hasLowConfidence || b.flags?.hasAskedHuman
      if (aHasFlag && !bHasFlag) return -1
      if (!aHasFlag && bHasFlag) return 1
      const aTime = a.lastMessage ? new Date(a.lastMessage.created_at).getTime() : 0
      const bTime = b.lastMessage ? new Date(b.lastMessage.created_at).getTime() : 0
      if (aTime !== bTime) return bTime - aTime
      return a.contact.id.localeCompare(b.contact.id)
    })
  }, [filteredByRisk, sortMode])

  // Stable display order: cache the last rendered ID order and refresh when sort mode,
  // membership, or *computed sort order* changes. (Do not key only on alphabetized IDs —
  // that ignored SLA/recent ordering until sort mode was toggled.)
  const stableOrderRef = useRef<string[]>([])
  const stableSortKeyRef = useRef<string>("")

  const stableSortedConversations = useMemo(() => {
    const orderSignature = sortedConversations.map(c => c.contact.id).join(",")
    const sortKey = `${sortMode}|${orderSignature}`

    if (stableSortKeyRef.current !== sortKey) {
      stableSortKeyRef.current = sortKey
      stableOrderRef.current = sortedConversations.map(c => c.contact.id)
    }

    // Render in stable order; new contacts not yet in stable order go to the end
    const idToConv = new Map(sortedConversations.map(c => [c.contact.id, c]))
    const ordered: typeof sortedConversations = []
    for (const id of stableOrderRef.current) {
      const conv = idToConv.get(id)
      if (conv) ordered.push(conv)
    }
    // Append any brand-new contacts not yet in the stable order
    for (const conv of sortedConversations) {
      if (!stableOrderRef.current.includes(conv.contact.id)) ordered.push(conv)
    }
    return ordered
  }, [sortedConversations, sortMode])

  const categorize = useCallback((conversation: Conversation): ConversationCategory => {
    const handledBy = getHandledByFromModerationData(conversation.contact.moderation_data)
      ?? (conversation.contact.hitl ? "human" : "bot")
    const pending = parsePendingFlag(conversation.contact.moderation_data)
    return categorizeConversation({ flags: conversation.flags, handledBy, pending })
  }, [getHandledByFromModerationData])

  const openConversations = useMemo(
    () => stableSortedConversations.filter(c => categorize(c) === "open"),
    [stableSortedConversations, categorize]
  )

  const pendingConversations = useMemo(
    () => stableSortedConversations.filter(c => categorize(c) === "pending"),
    [stableSortedConversations, categorize]
  )

  const resolvedConversations = useMemo(
    () => stableSortedConversations.filter(c => categorize(c) === "resolved"),
    [stableSortedConversations, categorize]
  )

  const otherConversations = useMemo(
    () => stableSortedConversations.filter(c => categorize(c) === "new"),
    [stableSortedConversations, categorize]
  )

  const visibleOtherConversations = useMemo(
    () => otherConversations.slice(0, visibleOtherCount),
    [otherConversations, visibleOtherCount]
  )

  const visiblePendingConversations = useMemo(
    () => pendingConversations.slice(0, visiblePendingCount),
    [pendingConversations, visiblePendingCount]
  )

  const visibleResolvedConversations = useMemo(
    () => resolvedConversations.slice(0, visibleResolvedCount),
    [resolvedConversations, visibleResolvedCount]
  )

  useEffect(() => {
    setVisibleOtherCount(OTHER_PAGE_SIZE)
  }, [otherConversations.length])

  useEffect(() => {
    setVisiblePendingCount(OTHER_PAGE_SIZE)
  }, [pendingConversations.length])

  useEffect(() => {
    setVisibleResolvedCount(OTHER_PAGE_SIZE)
  }, [resolvedConversations.length])

  const renderConversationRow = (conversation: Conversation) => {
    const isSelected   = conversation.contact.id === selectedContactId
    const isSynthetic  = conversation.contact.type === "synthetic"
    const handledBy = getHandledByFromModerationData(conversation.contact.moderation_data) ?? (conversation.contact.hitl ? "human" : "bot")
    const isHumanHandled = handledBy === "human"
    const flags        = conversation.flags
    const category     = categorize(conversation)
    const lastMsgTime  = conversation.lastMessage
      ? new Date(conversation.lastMessage.created_at).getTime() / 1000
      : 0

    return (
      <div
        key={conversation.contact.id}
        onClick={() => onSelectContact(conversation.contact.id)}
        className={cn(
          "pl-2 pr-3 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors",
          isSelected && "bg-muted",
          category === "open" && "border-l-2 border-l-red-500 bg-red-50/30",
          category === "pending" && "border-l-2 border-l-amber-500 bg-amber-50/30",
          category === "resolved" && "border-l-2 border-l-green-500 bg-green-50/20"
        )}
      >
        <div className="flex items-start justify-between">
          <div className="text-xs font-medium truncate flex-1">
            {conversation.contact.name || conversation.contact.id}
          </div>
          <div className="flex items-center gap-1.5 ml-2 shrink-0">
            {conversation.unreadCount > 0 && (
              <Badge className={cn(badgeStyles.unread, "bg-blue-600 text-white border-blue-700")}>
                {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
              </Badge>
            )}
            {lastMsgTime > 0 && (
              <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                {formatTimestamp(lastMsgTime)}
              </div>
            )}
          </div>
        </div>

        {conversation.lastMessage && (
          <p className="text-[10px] text-muted-foreground truncate mt-0.5">
            {truncateMessage(conversation.lastMessage.message || "", 60)}
          </p>
        )}

        <div className="flex items-center gap-1 flex-wrap mt-1">
          <Badge
            variant={isHumanHandled ? "default" : "secondary"}
            className={badgeStyles.base}
          >
            {isHumanHandled
              ? <><User className="h-2 w-2 mr-0.5" />Human</>
              : <><Bot className="h-2 w-2 mr-0.5" />Bot</>}
          </Badge>

          {isSynthetic && (
            <Badge variant="outline" className={badgeStyles.sim}>
              <FlaskConical className="h-2 w-2 mr-0.5" />Sim
            </Badge>
          )}

          {flags?.needsReply && (
            <Badge className={badgeStyles.needsReply}>
              <MessageCircle className="h-2 w-2 mr-0.5" />Needs Reply
            </Badge>
          )}

          {conversation.assignedOperator && isHumanHandled && (
            <Badge variant="outline" className={badgeStyles.assigned}>
              <UserCheck className="h-2 w-2 mr-0.5" />
              {(conversation.assignedOperator.name || "").split(" ")[0]}
            </Badge>
          )}
        </div>

        {/* Eval item badges — top item always visible, +N expands inline */}
        {(flags?.evalItems?.length ?? 0) > 0 && (() => {
          const items = flags!.evalItems!
          const isExpanded = expandedBadges.has(conversation.contact.id)
          const visible = isExpanded ? items : items.slice(0, 1)
          const hidden = items.length - 1

          const itemColorClass = (s: number) =>
            s >= 5 ? "bg-red-100 text-red-700 border-red-200" :
            s >= 4 ? "bg-orange-100 text-orange-700 border-orange-200" :
            s >= 3 ? "bg-amber-100 text-amber-700 border-amber-200" :
            s >= 2 ? "bg-yellow-100 text-yellow-700 border-yellow-200" :
                     "bg-gray-100 text-gray-600 border-gray-200"

          return (
            <div className="flex flex-wrap items-center gap-0.5 mt-0.5">
              {visible.map(item => (
                <Tooltip key={item.itemId}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Badge
                        variant="outline"
                        className={cn("text-[9px] px-1.5 py-0 h-4 font-medium border cursor-default whitespace-nowrap", itemColorClass(item.maxSeverity))}
                      >
                        {item.name}{item.count > 1 && <span className="ml-0.5 opacity-60">×{item.count}</span>}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[240px] text-xs space-y-1 p-2">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-[10px] text-muted-foreground">Sev {item.maxSeverity} · {Math.round(item.maxConfidence * 100)}% confidence</div>
                    {item.latestReasoning && <p className="text-[10px] text-muted-foreground leading-snug border-t pt-1">{item.latestReasoning}</p>}
                  </TooltipContent>
                </Tooltip>
              ))}
              {!isExpanded && hidden > 0 && (
                <button
                  onClick={e => toggleBadgeExpand(conversation.contact.id, e)}
                  className="text-[9px] px-1.5 h-4 rounded-full border border-border bg-muted/60 text-muted-foreground font-medium hover:bg-muted hover:text-foreground transition-colors"
                >
                  +{hidden}
                </button>
              )}
              {isExpanded && (
                <button
                  onClick={e => toggleBadgeExpand(conversation.contact.id, e)}
                  className="text-[9px] px-1.5 h-4 rounded-full border border-border bg-muted/60 text-muted-foreground font-medium hover:bg-muted hover:text-foreground transition-colors"
                >
                  less
                </button>
              )}
            </div>
          )
        })()}

        {/* Severity + moderation badges + SLA row for flagged contacts */}
        {(flags?.hasHighRisk || flags?.hasLowConfidence || flags?.hasAskedHuman || Object.keys(flags?.activeFlags || {}).length > 0) && (
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {flags?.contactSeverity != null && (() => {
              const lvl = flags.contactSeverity!
              const colorClass =
                lvl >= 5 ? "bg-red-600 text-white" :
                lvl >= 4 ? "bg-orange-500 text-white" :
                lvl >= 3 ? "bg-amber-400 text-amber-900" :
                lvl >= 2 ? "bg-yellow-200 text-yellow-800" :
                           "bg-gray-200 text-gray-700"
              return (
                <Badge className={cn("text-[9px] px-1 py-0 h-4 font-semibold shrink-0", colorClass)}>
                  <AlertTriangle className="h-2 w-2 mr-0.5 shrink-0" />
                  {lvl}
                </Badge>
              )
            })()}
            {flags?.hasAskedHuman && (
              <Badge className={cn(badgeStyles.askedHuman, "text-[9px] px-1 py-0 h-4")}>
                <Brain className="h-2 w-2 mr-0.5" />Asked Human
              </Badge>
            )}
            {flags?.hasLowConfidence && (
              <Badge className={cn(badgeStyles.lowConfidence, "text-[9px] px-1 py-0 h-4")}>
                <HelpCircle className="h-2 w-2 mr-0.5" />Low Confidence
              </Badge>
            )}
            {flags?.hasHighRisk && (
              <Badge className={cn(badgeStyles.highRisk, "text-[9px] px-1 py-0 h-4")}>
                <AlertTriangle className="h-2 w-2 mr-0.5" />High Risk
              </Badge>
            )}
            {(() => {
              const sla = getSlaInfo(flags?.slaDeadline)
              if (!sla) return null
              return (
                <span className={cn(
                  "text-[9px] font-medium flex items-center gap-0.5",
                  sla.status === "breached" ? "text-red-600" :
                  sla.status === "warning"  ? "text-amber-600" : "text-muted-foreground"
                )}>
                  <Clock className="h-2.5 w-2.5" />{sla.text}
                </span>
              )
            })()}
          </div>
        )}
      </div>
    )
  }

  const toggleSectionCollapsed = (category: ConversationCategory) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  /**
   * One collapsible category section. Pending and Resolved accumulate stale
   * entries nobody needs to revisit, so they take a visible/setVisible pair
   * for "Load more" paging, matching how New/Other already worked; Open has
   * no pager since it's meant to be short — a moderator's actual to-do list.
   */
  const renderCategorySection = (args: {
    category: ConversationCategory
    label: string
    headerClassName?: string
    conversations: Conversation[]
    visibleConversations?: Conversation[]
    visibleCount?: number
    onLoadMore?: () => void
  }) => {
    const { category, label, headerClassName, conversations, visibleConversations, visibleCount, onLoadMore } = args
    // Headers always render, so an empty section shows as "(0)" instead of disappearing
    const collapsed = collapsedSections.has(category)
    const shown = visibleConversations ?? conversations

    return (
      <div key={category} className="border-b">
        <button
          type="button"
          onClick={() => toggleSectionCollapsed(category)}
          className={cn(
            "w-full flex items-center gap-1 px-2 py-1.5 border-b text-left hover:bg-muted/40",
            headerClassName
          )}
        >
          {collapsed
            ? <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            : <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />}
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label} ({conversations.length})
          </span>
        </button>
        {!collapsed && (
          <div className="divide-y">
            {shown.map(renderConversationRow)}
            {onLoadMore && visibleCount != null && visibleCount < conversations.length && (
              <div className="p-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={onLoadMore}
                >
                  Load more contacts
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full border-r">
      {/* Search + collapse */}
      <div className="px-2 py-1.5 border-b flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            placeholder="Search"
            defaultValue={searchQuery}
            onChange={e => debouncedSearch(e.target.value)}
            className="pl-7 h-7 text-xs"
          />
        </div>
        {onCollapse && (
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onCollapse} title="Collapse sidebar">
            <PanelLeftClose className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Filters row */}
      <div className="px-2 py-1 border-b flex items-center gap-1.5 flex-wrap">
        <Select value={filter} onValueChange={v => onFilterChange(v as ConversationFilter)}>
          <SelectTrigger className="h-7 text-[10px] min-w-0 flex-1 max-w-[72px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All</SelectItem>
            <SelectItem value="open" className="text-xs">Open</SelectItem>
            <SelectItem value="pending" className="text-xs">Pending</SelectItem>
            <SelectItem value="resolved" className="text-xs">Resolved</SelectItem>
            <SelectItem value="flagged" className="text-xs">Flagged</SelectItem>
            <SelectItem value="human" className="text-xs">Human</SelectItem>
            <SelectItem value="bot" className="text-xs">Bot</SelectItem>
            <SelectItem value="mine" className="text-xs">Mine</SelectItem>
            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
          </SelectContent>
        </Select>

        <Select value={timeRange} onValueChange={v => setTimeRange(v as typeof timeRange)}>
          <SelectTrigger className="h-7 text-[10px] min-w-0 flex-1 max-w-[88px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All time</SelectItem>
            <SelectItem value="24h" className="text-xs">24 hours</SelectItem>
            <SelectItem value="week" className="text-xs">Past week</SelectItem>
            <SelectItem value="2weeks" className="text-xs">Past 2 weeks</SelectItem>
            <SelectItem value="month" className="text-xs">Past month</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortMode} onValueChange={v => setSortMode(v as "recent" | "sla")}>
          <SelectTrigger className="h-7 text-[10px] w-[72px] shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sla" className="text-xs">SLA</SelectItem>
            <SelectItem value="recent" className="text-xs">Recent</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className={cn("h-7 text-[10px] px-2 flex-1 min-w-0 max-w-[80px] justify-center rounded-md", activeFilterCount > 0 && "bg-muted/60 border-muted-foreground/25 font-medium")}>
              <Filter className="h-2.5 w-2.5 mr-0.5 shrink-0" />
              {activeFilterCount === 0 ? "Filters" : `${activeFilterCount}`}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="flex flex-col max-h-[80vh]">
              {/* Source */}
              <div className="p-2 border-b shrink-0">
                <div className="text-[10px] font-semibold text-muted-foreground pb-1 uppercase tracking-wide">Conversation Source</div>
                <div className="grid grid-cols-3 gap-1">
                  <Button
                    variant={sourceFilter === "real_only" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => onSourceFilterChange("real_only")}
                  >
                    Real
                  </Button>
                  <Button
                    variant={sourceFilter === "synthetic_only" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => onSourceFilterChange("synthetic_only")}
                  >
                    Sim
                  </Button>
                  <Button
                    variant={sourceFilter === "all" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => onSourceFilterChange("all")}
                  >
                    All
                  </Button>
                </div>
              </div>

              {/* SLA Status */}
              <div className="p-2 border-b shrink-0">
                <div className="text-[10px] font-semibold text-muted-foreground pb-1 uppercase tracking-wide">SLA Status</div>
                {[
                  { value: "unread",      label: "Unread",      icon: null },
                  { value: "needs-reply", label: "Needs Reply", icon: MessageCircle },
                  { value: "has-sla",    label: "Has SLA",     icon: null },
                  { value: "breached",   label: "SLA Breached", icon: null },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer text-xs">
                    <Checkbox checked={riskFilters.has(opt.value)} onCheckedChange={() => toggleRiskFilter(opt.value)} className="h-3 w-3" />
                    {opt.icon && <opt.icon className="h-3 w-3 text-muted-foreground shrink-0" />}
                    {opt.label}
                  </label>
                ))}
              </div>

              {/* Message Channel */}
              {messageChannels.length > 0 && (
                <div className="p-2 border-b shrink-0">
                  <div className="text-[10px] font-semibold text-muted-foreground pb-1 uppercase tracking-wide">
                    Message Channel ({messageChannels.length})
                  </div>
                  <div className="max-h-28 overflow-y-auto">
                    {messageChannels.map(({ name, count }) => (
                      <label key={name} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer text-xs">
                        <Checkbox
                          checked={channelFilters.has(name)}
                          onCheckedChange={() => toggleChannelFilter(name)}
                          className="h-3 w-3"
                        />
                        <span className="truncate flex-1">{name}</span>
                        <span className="text-[9px] text-muted-foreground shrink-0">{count}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Level */}
              <div className="p-2 border-b shrink-0">
                <div className="text-[10px] font-semibold text-muted-foreground pb-1 uppercase tracking-wide">Risk Level (Contact Severity)</div>
                {[
                  { value: "5", label: "5 - Critical", color: "text-red-600"    },
                  { value: "4", label: "4 - High",     color: "text-orange-600" },
                  { value: "3", label: "3 - Moderate", color: "text-amber-600"  },
                  { value: "2", label: "2 - Mild",     color: "text-green-600"  },
                  { value: "1", label: "1 - Low",      color: "text-blue-600"   },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer text-xs">
                    <Checkbox checked={riskFilters.has(opt.value)} onCheckedChange={() => toggleRiskFilter(opt.value)} className="h-3 w-3" />
                    <span className={opt.color}>{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Category — scrollable, searchable */}
              {riskCategories.length > 0 && (
                <div className="flex flex-col min-h-0 flex-1">
                  <div className="px-2 pt-2 pb-1 shrink-0">
                    <div className="text-[10px] font-semibold text-muted-foreground pb-1 uppercase tracking-wide">
                      Category ({riskCategories.length})
                    </div>
                    <div className="relative">
                      <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search categories..."
                        value={categorySearch}
                        onChange={e => setCategorySearch(e.target.value)}
                        className="w-full pl-5 pr-2 py-0.5 text-[10px] bg-muted/50 border border-border rounded h-5 outline-hidden focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1 px-2 pb-2 max-h-48">
                    {riskCategories
                      .filter(c => !categorySearch || c.name.toLowerCase().includes(categorySearch.toLowerCase()))
                      .map(({ name, count }) => (
                        <label key={name} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer text-xs">
                          <Checkbox checked={riskFilters.has(name)} onCheckedChange={() => toggleRiskFilter(name)} className="h-3 w-3" />
                          <span className="truncate flex-1">{name}</span>
                          <span className="text-[9px] text-muted-foreground shrink-0">{count}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              {activeFilterCount > 0 && (
                <div className="p-2 border-t shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-6 text-[10px]"
                    onClick={() => {
                      setRiskFilters(new Set())
                      setChannelFilters(new Set())
                      onSourceFilterChange("real_only")
                    }}
                  >
                    Clear {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""}
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Section header */}
      {!loading && timeFilteredConversations.length > 0 && (
        <div className="px-2 py-1.5 border-b flex items-center gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {riskFilters.size > 0
              ? `Filtered (${stableSortedConversations.length})`
              : filter === "flagged"
                ? `Flagged (${stableSortedConversations.length})`
                : filter === "all"
                  ? `All Conversations (${stableSortedConversations.length})`
                  : `${filter.charAt(0).toUpperCase() + filter.slice(1)} (${stableSortedConversations.length})`}
          </span>
        </div>
      )}

      {/* Radix inner wrapper uses display:table — !block fixes layout. Do NOT pad this wrapper: that leaves a white gutter and clips row backgrounds. Pad each row instead so hover/selected bg reaches the edge. */}
      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        {loading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            <div className="text-center">
              {/*
                An in-flight search must not read as "no matches" — the query
                covers the full history server-side and may still return hits.
              */}
              {searching ? (
                <>
                  <Loader2 className="h-6 w-6 mx-auto mb-1.5 animate-spin opacity-50" />
                  <p className="text-xs">Searching all messages…</p>
                </>
              ) : (
                <>
                  <Users className="h-8 w-8 mx-auto mb-1 opacity-50" />
                  <p className="text-xs">No conversations found</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div>
            {renderCategorySection({
              category: "open",
              label: "Open",
              headerClassName: "bg-red-50/50",
              conversations: openConversations,
            })}

            {renderCategorySection({
              category: "pending",
              label: "Pending",
              headerClassName: "bg-amber-50/50",
              conversations: pendingConversations,
              visibleConversations: visiblePendingConversations,
              visibleCount: visiblePendingCount,
              onLoadMore: () => setVisiblePendingCount(prev => prev + OTHER_PAGE_SIZE),
            })}

            {renderCategorySection({
              category: "resolved",
              label: "Resolved",
              headerClassName: "bg-green-50/50",
              conversations: resolvedConversations,
              visibleConversations: visibleResolvedConversations,
              visibleCount: visibleResolvedCount,
              onLoadMore: () => setVisibleResolvedCount(prev => prev + OTHER_PAGE_SIZE),
            })}

            {renderCategorySection({
              category: "new",
              label: "New",
              headerClassName: "bg-muted/20",
              conversations: otherConversations,
              visibleConversations: visibleOtherConversations,
              visibleCount: visibleOtherCount,
              onLoadMore: () => setVisibleOtherCount(prev => prev + OTHER_PAGE_SIZE),
            })}

            {/*
              The list is backed by a bounded window of recent messages, so
              conversations whose last activity predates it have no preview
              yet. This loads an older slice.

              Search does NOT depend on this — it runs against the full
              history server-side — so it is hidden while searching to avoid
              implying results are incomplete.
            */}
            {hasMoreMessages && !searchQuery && (
              <div className="px-3 py-3 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={loadingMoreMessages}
                  onClick={onLoadMoreMessages}
                >
                  {loadingMoreMessages ? (
                    <><Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> Loading…</>
                  ) : (
                    "Load older conversations"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
